import { ReactNode, useEffect } from 'react';

import { NavDrawerContext } from 'components/navigation/nav_drawer/context';
import { SIDENAV_CONFIG } from 'components/navigation/side_nav/config';
import { useSuperState, useWindowDimensions } from 'hooks';

export type NavDrawerContextValue = {
    close: () => void;
    isMobile: boolean;
    isOpen: boolean;
    open: () => void;
    toggle: () => void;
};

export type NavDrawerProviderProps = {
    children: ReactNode;
};

/**
 * Shares the mobile drawer state between the SideNav (which renders the drawer +
 * backdrop) and the TopNav (which renders the hamburger toggle).
 *
 * `isMobile` is derived from `useWindowDimensions().width`, which is correct on the
 * very first render. (The hook's `isBelow` flag resolves a tick later via a layout
 * effect, which would briefly mount the sidenav in non-drawer mode and animate it
 * closed — using `width` avoids that flash.)
 */
export const NavDrawerProvider = (props: NavDrawerProviderProps) => {

    const { children } = props;

    const [state, setState] = useSuperState({
        isOpen: false
    });

    const { width } = useWindowDimensions();
    const isMobile = width < SIDENAV_CONFIG.MOBILE;

    const open = () => setState({ isOpen: true });
    const close = () => setState({ isOpen: false });
    const toggle = () => setState({ isOpen: !state.isOpen });

    // Force the drawer closed when the viewport grows past the mobile breakpoint
    // so it never stays stuck open over the restored rail.
    useEffect(() => {

        if (!isMobile && state.isOpen) {

            setState({ isOpen: false });

        }

    }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

    const context: NavDrawerContextValue = {
        close,
        isMobile,
        isOpen: state.isOpen,
        open,
        toggle
    };

    return (
        <NavDrawerContext.Provider value={context}>
            {children}
        </NavDrawerContext.Provider>
    );

};
